import mixpanel from "mixpanel-browser";
import type { ResolvedConfig } from "@/config";
import { DEFAULT_MIXPANEL_API_HOST } from "@/types/config.types";

/**
 * One-time Mixpanel browser SDK lifecycle.
 * Skips cleanly when Mixpanel is disabled or the project token is empty.
 */
export class MixpanelService {
	private static initialized = false;
	private static initPromise: Promise<void> | null = null;
	private static environment = "";

	static async initialize(config: ResolvedConfig): Promise<void> {
		if (MixpanelService.initPromise) {
			return MixpanelService.initPromise;
		}
		if (MixpanelService.initialized) {
			return Promise.resolve();
		}

		MixpanelService.initPromise = MixpanelService.performInitialization(config);
		return MixpanelService.initPromise;
	}

	static isInitialized(): boolean {
		return MixpanelService.initialized;
	}

	static identify(userId: string, email: string): void {
		if (!MixpanelService.initialized || !userId) return;

		mixpanel.identify(userId);
		if (email) {
			mixpanel.people.set({ $email: email });
		}
	}

	static setOrganization(organizationId: string, organizationName?: string): void {
		if (!MixpanelService.initialized || !organizationId) return;

		if (organizationName) {
			mixpanel.register({
				organization_id: organizationId,
				organization_name: organizationName,
			});
			return;
		}

		mixpanel.register({ organization_id: organizationId });
		mixpanel.unregister("organization_name");
	}

	static clearOrganization(): void {
		if (!MixpanelService.initialized) return;
		mixpanel.unregister("organization_id");
		mixpanel.unregister("organization_name");
	}

	static reset(): void {
		if (!MixpanelService.initialized) return;
		mixpanel.reset();
		if (MixpanelService.environment) {
			mixpanel.register({ environment: MixpanelService.environment });
		}
	}

	/** Test-only. Clears the in-memory init guard. */
	static resetForTests(): void {
		MixpanelService.initialized = false;
		MixpanelService.initPromise = null;
		MixpanelService.environment = "";
	}

	private static async performInitialization(
		config: ResolvedConfig,
	): Promise<void> {
		try {
			const mixpanelConfig = config.integrations?.mixpanel;
			if (!mixpanelConfig?.enabled || !mixpanelConfig.projectToken.trim()) {
				return;
			}

			const environment = config.app.environment;
			MixpanelService.environment = environment;

			mixpanel.init(mixpanelConfig.projectToken, {
				api_host: mixpanelConfig.apiHost || DEFAULT_MIXPANEL_API_HOST,
				persistence: "localStorage",
				debug: environment === "local" || environment === "development",
				record_sessions_percent: 1,
				autocapture: {
					pageview: "url-with-path",
					click: true,
					submit: true,
					input: false,
					scroll: false,
					capture_text_content: false,
					capture_extra_attrs: ["data-testid"],
					rage_click: false,
					dead_click: false,
				},
			});
			mixpanel.register({ environment });
			MixpanelService.initialized = true;
		} catch (error) {
			console.error("Mixpanel initialization failed:", error);
		} finally {
			MixpanelService.initPromise = null;
		}
	}
}
